import React, { FC } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { Briefcase } from '@gravity-ui/icons';
import Gear from '@gravity-ui/icons/Gear';
import { Avatar, Button, Icon, Label, Link as GravityLink } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import { User } from '@yandex-lego/user';
import clx from 'classnames';
import { routes } from 'client/lib/routes';
import { ClientConfig } from 'client/types/global';
import { getIsYangoRequest } from 'client/utils';
import * as generalKeyset from 'i18n/GeneralSettings';
import * as keyset from 'i18n/Header';

import { Logo } from '../icons/Logo';
import { LangSwitcher } from '../LangSwitcher/LangSwitcher';

import styles from './Header.module.scss';

const t = i18n(keyset);
const tGeneral = i18n(generalKeyset);

interface UserWithPassport {
    user: ClientConfig['user'];
    size?: 's';
    withName?: boolean;
}

interface UserWithPassportModalProps {
    login: string;
    porgName?: string;
    size?: 's';
}

interface IHeaderIndexProps {
    hideSettings?: boolean;
}

interface IHeaderQuickApps {
    startPage?: boolean;
}

const UserWithPassportLink: FC<UserWithPassport> = ({ size, user, withName = true }) => {
    const { passport } = __appConfig;
    const { pathname, search } = useLocation();
    const href = encodeURIComponent(window.location.origin + pathname + search);
    const passportLink = `${passport}?retpath=${href}`;
    return (
        <GravityLink className={styles.passportLinkWrapper} href={passportLink}>
            <Avatar
                className={styles.header__avatar}
                imgUrl={`https://avatars.mds.yandex.net/get-yapic/${user.blackbox.avatar?.default || '0/0-0'}/islands-middle`}
                size={size === 's' ? 'xs' : undefined}
            />
            {withName ? user.blackbox.displayName : null}
        </GravityLink>
    );
};

const UserWithPassportModal: FC<UserWithPassportModalProps> = ({ login, size, porgName }) => {
    const { passport } = __appConfig;
    const avatarSize = size === 's' ? 24 : 32;

    let displayUserLogin = <span className={styles.login}>{login}</span>;
    if (porgName) {
        displayUserLogin = (
            <Button className={styles.porgContainer} title={porgName} size="m">
                <Icon data={Briefcase} size={16} />
                <span className={styles.porgName}>{porgName}</span>
            </Button>
        );
    }
    return (
        <User
            avatarSize={avatarSize}
            env={passport?.includes('test') ? 'test' : 'prod'}
            fetchUserData
            afterAvatar={displayUserLogin}
            queryParams={{
                porganizations: '1',
            }}
        />
    );
};

const UserPassport: FC<UserWithPassport> = ({ size, user }) => {
    if (getIsYangoRequest()) {
        return <UserWithPassportLink size={size} user={user} />;
    }
    return <UserWithPassportModal size={size} login={user.blackbox.displayName} porgName={user.porgName} />;
};

export const Header = () => {
    const { user } = __appConfig;
    return (
        <header className={clx(styles.header, styles.header_settingPage)}>
            <Link className={clx(styles.logo__container, styles.logo__container_settingPage)} to={routes.app.root}>
                {getIsYangoRequest() ? (
                    <img className={styles.yangoLogo} src={routes.app.assets('images/yango.svg')} />
                ) : (
                    <Logo />
                )}
            </Link>
            <div className={styles.rightHeaderBlock}>
                {user && (
                    <>
                        <Link className={styles.header__link} to={routes.app.root}>
                            {t('projects')}
                        </Link>
                        {getIsYangoRequest() ? null : (
                            <Link
                                className={clx(styles.header__link, styles.header__link_hideInMobile)}
                                to={routes.app.settings()}
                            >
                                <Gear />
                                {tGeneral('header-settings')}
                            </Link>
                        )}
                        <LangSwitcher />
                        <UserPassport size="s" user={user} />
                    </>
                )}
            </div>
        </header>
    );
};

export const HeaderIndex: FC<IHeaderIndexProps> = ({ hideSettings }) => {
    const { user } = __appConfig;
    return (
        <header className={clx(styles.header, styles.header_indexPage)}>
            <Link className={clx(styles.logo__container, styles.logo__container_indexPage)} to={routes.app.root}>
                {getIsYangoRequest() ? (
                    <img className={styles.yangoLogo} src={routes.app.assets('images/yango.svg')} />
                ) : (
                    <Logo />
                )}
            </Link>
            <div className={styles.rightHeaderBlock}>
                {user && (
                    <>
                        {getIsYangoRequest() || hideSettings ? null : (
                            <Link
                                className={clx(styles.header__link, styles.header__link_hideInMobile)}
                                to={routes.app.settings()}
                            >
                                <Gear />
                                {tGeneral('header-settings')}
                            </Link>
                        )}
                        <LangSwitcher />
                        <UserPassport user={user} />
                    </>
                )}
            </div>
        </header>
    );
};

export const HeaderQuickApps: FC<IHeaderQuickApps> = ({ startPage }) => {
    const { user } = __appConfig;
    return (
        <header className={clx(styles.header, startPage ? styles.header_quickAppsStart : styles.header_quickApps)}>
            <Link className={clx(styles.logo__container, styles.logo__container_settingPage)} to={routes.app.root}>
                <Logo />
                {__appConfig.app.showQuickAppsPageToAllUsers ? null : (
                    <Label className={styles.logo__ndaBadge} size="m">
                        {'NDA'}
                    </Label>
                )}
            </Link>
            <div className={styles.rightHeaderBlock}>
                {user && <UserWithPassportLink user={user} size="s" withName={false} />}
            </div>
        </header>
    );
};
